import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SwiperSlide } from 'swiper/react';
import { withTranslation } from 'react-i18next';
import SwiperWrapper from '@components/SwiperWrapper';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { CrossPlusIcon } from '@icons/CrossPlusIcon';

import { IUser } from '@interfaces/user';
import { ISelectAvatarContentProps } from './interfaces';
import SelectAvatarItem from './SelectAvatarItem/SelectAvatarItem';
import ROUTES from '@constants/routes';
import { NOTIFICATION_TYPES, useNotification } from '@components/Notification';
import { getNotification } from '@components/Notification/helpers';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import { AnalyticsType } from '@interfaces/analytics';
import { AvatarTypes, IUserAvatar } from '@interfaces/avatar';
import ParticipantsService from '@services/ParticipantsService';
import { useStyles } from './styles';
import ConfirmDeleteAvatarModal from './ConfirmDeleteAvatarModal';
import {
  useClientSpecificCanCreateNewAvatar,
  useClientSpecificAvatarDeletion,
  useClientSpecificHideAvaturnCreation,
} from '@hooks/clientSpecificUtils';
import { useAvaturnHelper } from '@hooks/clientSpecificHooks';

const SelectAvatarContent = ({
  avatars,
  selectedAvatar,
  setSelectedAvatar,
  t: translate,
}: ISelectAvatarContentProps) => {
  const navigate = useNavigate();
  const { currentEventId, trackEventAnalytics } = useMytaverseEvent();
  const {
    setWantsToCreateAvatar,
    setUser,
    setUserAvatars,
    setAvaturnToProcess,
    setIsCreatingGenericAvaturn,
  } = useMytaverse();
  const { setCurrentAvatarId, setCustomAvatarUrl } = useMytaverse();
  const classes = useStyles();
  const { showNotification } = useNotification();
  const { deleteAvatar, getProcessedBrand } = useAvaturnHelper();
  const brand = getProcessedBrand();

  const [openConfirmDeleteAvatarModal, setOpenConfirmDeleteAvatarModal] =
    useState<boolean>(false);
  const [avatarToDelete, setAvatarToDelete] = useState<IUserAvatar | null>(
    null,
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const canCreateNewAvatar = useClientSpecificCanCreateNewAvatar(avatars);

  const clientSpecificAvatarDeletion = useClientSpecificAvatarDeletion();
  const hideAvaturnCreation = useClientSpecificHideAvaturnCreation();

  const createNewAvatar = useCallback(() => {
    if (canCreateNewAvatar) {
      setWantsToCreateAvatar(true);
      setCurrentAvatarId(null);
      setCustomAvatarUrl(null);
      navigate(ROUTES.SELECT_AVATAR_PROVIDER(currentEventId));
    } else {
      showNotification(
        getNotification({
          message: translate('notifications.cantCreateMoreAvtars'),
          type: NOTIFICATION_TYPES.WARNING,
          withDismissButton: false,
          withReloadButton: false,
        }),
      );
    }
  }, [currentEventId, avatars, canCreateNewAvatar]);

  const editAvatarClick = useCallback(
    async (avatar: IUserAvatar) => {
      setCurrentAvatarId(avatar?.avatarId ? avatar.avatarId : null);
      setCustomAvatarUrl(avatar?.avatarUrl ? avatar.avatarUrl : null);
      trackEventAnalytics(AnalyticsType.AvatarCustomize);

      switch (avatar?.avatarType) {
        case AvatarTypes.AVATURN:
          // we are editing an existing avaturn. We need to set the avaturn we will be processing,
          // and notify this is not a generic avaturn avatar creation
          setAvaturnToProcess({
            avatarId: avatar?.avatarId,
            avaturnId: '',
          });
          setIsCreatingGenericAvaturn(false);
          await ParticipantsService.updateCurrentParticipantAvatar({
            activeAvatarId: avatar?.avatarId,
          });
          navigate(ROUTES.AVATURN(currentEventId));
          break;
        case AvatarTypes.READY_PLAYER_ME:
          // do something else?
          break;
        default:
          // do something else?
          break;
      }
    },
    [
      navigate,
      setCurrentAvatarId,
      setCustomAvatarUrl,
      trackEventAnalytics,
      currentEventId,
      setAvaturnToProcess,
      setIsCreatingGenericAvaturn,
    ],
  );

  const deleteAvatarClick = useCallback((avatar: IUserAvatar) => {
    setAvatarToDelete(avatar);
    setOpenConfirmDeleteAvatarModal(true);
  }, []);

  const handleDeletionAvatar = useCallback(async () => {
    if (!avatarToDelete) {
      return;
    }

    try {
      setIsProcessing(true);
      const avatars = await deleteAvatar(avatarToDelete.avatarId, brand);

      // clearing everything after deletion
      const userUpdate = {
        activeAvatarId: '',
      };
      await ParticipantsService.updateCurrentParticipantAvatar(userUpdate);

      await clientSpecificAvatarDeletion(avatarToDelete);

      setCurrentAvatarId('');
      setCustomAvatarUrl('');
      setUser((prevState: IUser) => ({
        ...prevState,
        ...userUpdate,
        avatars: avatars,
      }));
      setUserAvatars(avatars);
      setSelectedAvatar(null);
      setOpenConfirmDeleteAvatarModal(false);
      setIsProcessing(false);
    } catch {
      setIsProcessing(false);
    }
  }, [avatarToDelete, clientSpecificAvatarDeletion, deleteAvatar, brand]);

  return (
    <>
      <SwiperWrapper>
        {!hideAvaturnCreation ? (
          <SwiperSlide key="createnewavatar">
            <Box sx={classes.createRoot} onClick={createNewAvatar}>
              <Card sx={classes.createCard}>
                <CardContent sx={classes.createContent}>
                  <CrossPlusIcon sx={classes.createIcon} />
                  <Typography sx={classes.createText}>
                    {translate('userAvatarPage.createNewAvatar')}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </SwiperSlide>
        ) : null}
        {avatars.map((avatar) => (
          <SwiperSlide key={avatar.avatarId}>
            <SelectAvatarItem
              avatar={avatar}
              isSelectedAvatar={selectedAvatar?.avatarId === avatar.avatarId}
              setSelectedAvatar={setSelectedAvatar}
              deleteAvatarClick={deleteAvatarClick}
              editAvatarClick={editAvatarClick}
            />
          </SwiperSlide>
        ))}
      </SwiperWrapper>
      {avatarToDelete && (
        <ConfirmDeleteAvatarModal
          open={openConfirmDeleteAvatarModal}
          handleCancel={() => setOpenConfirmDeleteAvatarModal(false)}
          handleConfirm={handleDeletionAvatar}
          avatarToDelete={avatarToDelete}
          isProcessing={isProcessing}
        />
      )}
    </>
  );
};

export default withTranslation()(SelectAvatarContent);
